/**
 * DEV-ONLY mock layer. Lets you review the full UI in the browser before/without the backend.
 *
 * Activated only when BOTH are true:
 *   - the build is a Vite dev build  (import.meta.env.DEV)
 *   - VITE_BYPASS_AUTH=true in src/ui/.env
 *
 * In a production build `import.meta.env.DEV` is the constant `false`, so MOCK_ENABLED folds to
 * `false`, the `api` ternary in lib/api.ts resolves to the real client, and Rollup tree-shakes this
 * module (and its JSON fixtures) out of the bundle. Nothing here ships to prod.
 *
 * Domain-scoped model (ADR-0006 + spec 002 refinement): one snapshot decomposed into per-domain
 * records; portfolios are first-class. `getDashboard` reads the signed-in user from sessionStorage
 * and returns only that user's visible domains — so scoping behaves like the real backend.
 */
import {normalizePortfolio} from '@ait-tracker/shared/src/types';
import {diffDomains, extractSnapshot, type CurrentDomain} from '@ait-tracker/shared/src/file-schema';
import type {
  AdminDomain,
  ManagedUser,
  NewUser,
  Portfolio,
  PortfolioInput,
  SessionUser,
  UploadDiff,
  UserUpdate
} from '../lib/api';
import {DEMO_USER, ROLE_PREVIEWS} from './personas';
import portfolioIops from './fixtures/portfolio-iops.json';
import portfolioTms from './fixtures/portfolio-tms.json';

export const MOCK_ENABLED = import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === 'true';

const delay = <T>(value: T): Promise<T> => new Promise(resolve => setTimeout(() => resolve(value), 200));

type RawDomain = {id: number | string; name: string; jtbds: unknown[]; [k: string]: unknown};
type Chrome = {_meta?: unknown; phases: string[]; phaseColors?: unknown; serviceAreas: unknown[]};

const chrome = portfolioIops as unknown as Chrome;
const CATS = ['cads', 'ete', 'ers', 'et', '_unassigned'];

// One combined snapshot decomposed into per-domain records, each with a cycling portfolio.
interface MockDomain {
  domainId: string;
  name: string;
  portfolio: string;
  active: boolean;
  content: RawDomain;
}
const DOMAINS: MockDomain[] = [
  ...(portfolioIops as unknown as {domains: RawDomain[]}).domains,
  ...(portfolioTms as unknown as {domains: RawDomain[]}).domains
]
  .filter((d, i, all) => all.findIndex(x => String(x.id) === String(d.id)) === i)
  .map((content, i) => ({
    domainId: String(content.id),
    name: content.name,
    portfolio: CATS[i % CATS.length],
    active: true,
    content
  }));

// First-class portfolios (mutable store so create/update/delete round-trip in the mock).
const MOCK_PORTFOLIOS: Omit<Portfolio, 'domainCount'>[] = [
  {portfolioId: 'cads', name: 'CADS', description: 'Cox Automotive Data Solutions'},
  {portfolioId: 'ete', name: 'ETE', description: 'Enterprise Technology & Engineering'},
  {portfolioId: 'ers', name: 'ERS', description: 'Enterprise Risk & Security'},
  {portfolioId: 'et', name: 'ET', description: 'Enterprise Transformation'}
];

const VERSIONS = [
  {uploadedAt: '2026-06-28T16:30:00.000Z', uploadedBy: 'erin.admin@coxautoinc.com'},
  {uploadedAt: '2026-06-14T09:05:00.000Z', uploadedBy: 'erin.admin@coxautoinc.com'}
];

/** Staged (previewed-but-not-committed) uploads, keyed by mock uploadId (spec 003 two-step flow). */
const STAGED = new Map<string, unknown>();

/** The signed-in user — AuthContext stores it under `ait_user`. */
function effectiveUser(): SessionUser | null {
  const raw = sessionStorage.getItem('ait_user');
  return raw ? (JSON.parse(raw) as SessionUser) : null;
}

/** Domains visible to the effective user: admin→all; viewer→portfolios ∪ ids; owner→ids. */
function visibleDomains(): MockDomain[] {
  const u = effectiveUser();
  if (!u) return [];
  if (u.roles.includes('admin')) return DOMAINS.filter(d => d.active);
  const isViewer = u.roles.includes('portfolio-viewer');
  const isOwner = u.roles.includes('domain-owner');
  const cats = isViewer ? u.assignedPortfolios.filter(c => c !== '_unassigned') : [];
  const ids = isViewer || isOwner ? u.assignedDomainIds : [];
  return DOMAINS.filter(d => d.active && (cats.includes(d.portfolio) || ids.includes(d.domainId)));
}

/**
 * Mirrors the API's `entitledPortfolios`: admin → every portfolio that exists (including empty ones);
 * non-admin → their portfolio grants PLUS the portfolios of their individually granted domains, so a
 * granted out-of-portfolio domain has a pill to be reached through.
 */
function entitledPortfolios(): string[] {
  const u = effectiveUser();
  if (!u) return [];
  if (u.roles.includes('admin')) {
    const ids = new Set(MOCK_PORTFOLIOS.map(p => p.portfolioId));
    for (const d of DOMAINS) if (d.active && d.portfolio !== '_unassigned') ids.add(d.portfolio);
    return [...ids];
  }
  const reachable = new Set(u.assignedPortfolios.filter(p => p !== '_unassigned'));
  for (const d of visibleDomains()) if (d.portfolio !== '_unassigned') reachable.add(d.portfolio);
  return [...reachable];
}

const MOCK_USERS: ManagedUser[] = [DEMO_USER, ...ROLE_PREVIEWS.map(p => p.user)].map(u => ({
  email: u.email,
  displayName: u.displayName,
  roles: u.roles,
  assignedPortfolios: u.assignedPortfolios,
  assignedDomainIds: u.assignedDomainIds,
  enabled: true,
  createdAt: '2026-06-01T00:00:00.000Z',
  updatedAt: '2026-06-01T00:00:00.000Z'
}));

function portfolioView(): Portfolio[] {
  const counts = new Map<string, number>();
  for (const d of DOMAINS) if (d.active) counts.set(d.portfolio, (counts.get(d.portfolio) ?? 0) + 1);
  return MOCK_PORTFOLIOS.map(p => ({...p, domainCount: counts.get(p.portfolioId) ?? 0})).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
}

export const mockApi = {
  logout: () => {
    sessionStorage.removeItem('ait_user');
    return delay({success: true});
  },

  // SSO is not exercised in mock mode (no server/cookie); resolve the demo user for shape parity.
  getMe: () => delay({user: DEMO_USER}),

  getDashboard: (_version?: string) =>
    delay({
      _meta: chrome._meta ?? {},
      phases: chrome.phases,
      phaseColors: chrome.phaseColors ?? {},
      serviceAreas: chrome.serviceAreas,
      portfolios: entitledPortfolios(),
      domains: visibleDomains().map(d => ({...d.content, portfolio: d.portfolio}))
    }),

  listSnapshots: () => delay({versions: [...VERSIONS]}),

  // Step 1 (spec 003): diff the staged file against the chris-v1 mock domains (by id) — no writes.
  previewSnapshot: (file: unknown): Promise<UploadDiff> => {
    const {domains: incoming} = extractSnapshot(file);
    const current: CurrentDomain[] = DOMAINS.filter(d => d.active).map(d => ({
      domainId: d.domainId,
      name: d.name,
      portfolio: d.portfolio
    }));
    const {added, removed, renamed, unchanged} = diffDomains(incoming, current);
    const removedAnnotated = removed.map(d => ({
      ...d,
      ownerGrantCount: MOCK_USERS.filter(u => u.assignedDomainIds.includes(d.domainId)).length
    }));
    const uploadId = `mock-${Date.now()}`;
    STAGED.set(uploadId, file);
    return delay({uploadId, added, removed: removedAnnotated, renamed, unchanged});
  },

  // Step 2 (spec 003): apply the previewed reconcile to the mock store. Identity is the domain id,
  // so a renamed domain (same id, new name) updates in place and keeps its portfolio automatically.
  commitSnapshot: (uploadId: string) => {
    const file = STAGED.get(uploadId);
    STAGED.delete(uploadId);
    const {domains: incoming} = file ? extractSnapshot(file) : {domains: []};
    const incomingIds = new Set(incoming.map(d => d.domainId));

    for (const d of incoming) {
      const existing = DOMAINS.find(x => x.domainId === d.domainId);
      if (existing) {
        existing.name = d.name; // rename in place — portfolio untouched
        existing.content = d.content as RawDomain;
        existing.active = true;
      } else {
        DOMAINS.push({
          domainId: d.domainId,
          name: d.name,
          portfolio: '_unassigned',
          active: true,
          content: d.content as RawDomain
        });
      }
    }
    // Retire domains absent from the upload.
    for (const d of DOMAINS) {
      if (!incomingIds.has(d.domainId)) d.active = false;
    }
    return delay({uploadedAt: new Date().toISOString(), uploadedBy: DEMO_USER.email, domainCount: incoming.length});
  },

  listDomains: () =>
    delay({
      domains: DOMAINS.filter(d => d.active)
        .map<AdminDomain>(d => ({domainId: d.domainId, name: d.name, portfolio: d.portfolio, active: d.active}))
        .sort((a, b) => a.name.localeCompare(b.name))
    }),

  setDomainPortfolio: (domainId: string, portfolio: string) => {
    const d = DOMAINS.find(x => x.domainId === domainId);
    if (d) d.portfolio = normalizePortfolio(portfolio);
    return delay({domainId, portfolio: d?.portfolio ?? portfolio});
  },

  deleteDomain: (domainId: string) => {
    const i = DOMAINS.findIndex(x => x.domainId === domainId);
    if (i >= 0) DOMAINS.splice(i, 1);
    return delay({domainId, deleted: i >= 0});
  },

  // ── portfolios (first-class) ──
  listPortfolios: () => delay({portfolios: portfolioView()}),

  createPortfolio: (input: PortfolioInput) => {
    const name = (input.name ?? '').trim();
    const portfolioId = normalizePortfolio(name);
    const created: Portfolio = {portfolioId, name, description: input.description, domainCount: 0};
    MOCK_PORTFOLIOS.push({portfolioId, name, description: input.description});
    return delay(created);
  },

  updatePortfolio: (portfolioId: string, input: PortfolioInput) => {
    const p = MOCK_PORTFOLIOS.find(x => x.portfolioId === portfolioId);
    if (!p) return Promise.reject(new Error(`[mock] no portfolio "${portfolioId}"`));
    if (input.name !== undefined) p.name = input.name.trim();
    if (input.description !== undefined) p.description = input.description.trim() || undefined;
    return delay({...p, domainCount: DOMAINS.filter(d => d.active && d.portfolio === portfolioId).length});
  },

  deletePortfolio: (portfolioId: string) => {
    const idx = MOCK_PORTFOLIOS.findIndex(x => x.portfolioId === portfolioId);
    let rehomed = 0;
    for (const d of DOMAINS) if (d.portfolio === portfolioId) (d.portfolio = '_unassigned'), rehomed++;
    if (idx >= 0) MOCK_PORTFOLIOS.splice(idx, 1);
    return delay({portfolioId, rehomedDomains: rehomed});
  },

  setPortfolioDomains: (portfolioId: string, domainIds: string[]) => {
    const wanted = new Set(domainIds);
    for (const d of DOMAINS) {
      if (wanted.has(d.domainId)) d.portfolio = portfolioId;
      else if (d.portfolio === portfolioId) d.portfolio = '_unassigned';
    }
    return delay({portfolioId, domainIds: [...wanted]});
  },

  // ── users ──
  listUsers: () => delay({users: [...MOCK_USERS]}),

  createUser: (user: NewUser) => {
    const created: ManagedUser = {
      email: user.email.trim().toLowerCase(),
      displayName: user.displayName,
      roles: user.roles,
      assignedPortfolios: user.assignedPortfolios ?? [],
      assignedDomainIds: user.assignedDomainIds ?? [],
      enabled: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    MOCK_USERS.push(created);
    return delay(created);
  },

  updateUser: (email: string, update: UserUpdate) => {
    const u = MOCK_USERS.find(x => x.email === email);
    if (!u) return Promise.reject(new Error(`[mock] no user "${email}"`));
    Object.assign(u, {
      displayName: update.displayName ?? u.displayName,
      roles: update.roles ?? u.roles,
      assignedPortfolios: update.assignedPortfolios ?? u.assignedPortfolios,
      assignedDomainIds: update.assignedDomainIds ?? u.assignedDomainIds,
      enabled: update.enabled ?? u.enabled,
      updatedAt: new Date().toISOString()
    });
    return delay({...u});
  }
};
